/**
 * Lithuanian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const lt: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Valdymo skydelis',
  'nav.funds': 'Fondai',
  'nav.investments': 'Investicijos',
  'nav.investees': 'Investicijų gavėjai',
  'nav.esg': 'ESG Apžvalga',
  'nav.compliance': 'Atitiktis',
  'nav.reports': 'Ataskaitos',
  'nav.settings': 'Nustatymai',
  'nav.users': 'Naudotojai',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Rodikliai',
  'nav.taxonomy': 'ES Taksonomija',
  'nav.filings': 'Pateikimai',
  'nav.calendar': 'Kalendorius',
  'nav.submit': 'Pateikti duomenis',
  'nav.submissions': 'Mano pateikimai',
  'nav.benchmarks': 'Lyginamieji rodikliai',
  'nav.messages': 'Žinutės',
  'nav.portfolio': 'Portfelis',
  'nav.performance': 'Fondų rezultatai',
  'nav.esgSummary': 'ESG Santrauka',
  'nav.pipeline': 'Konvejeris',
  'nav.dueDiligence': 'Išsamus patikrinimas',
  'nav.impact': 'Poveikio rodikliai',
  'nav.sdg': 'DVT Žemėlapis',
  'nav.audit': 'Audito pėdsakas',
  'nav.snapshots': 'Duomenų momentinės kopijos',
  'nav.knowledgeGraph': 'Žinių grafas',
  'nav.geographic': 'Geografinis',
  'nav.bi': 'BI Studija',
  'nav.ai': 'AI Asistentas',
  'nav.tasks': 'Užduotys',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Reguliacinis monitorius',

  // Common Actions
  'action.create': 'Sukurti',
  'action.edit': 'Redaguoti',
  'action.delete': 'Ištrinti',
  'action.save': 'Išsaugoti',
  'action.cancel': 'Atšaukti',
  'action.submit': 'Pateikti',
  'action.approve': 'Patvirtinti',
  'action.reject': 'Atmesti',
  'action.download': 'Atsisiųsti',
  'action.export': 'Eksportuoti',
  'action.import': 'Importuoti',
  'action.filter': 'Filtruoti',
  'action.search': 'Ieškoti',
  'action.refresh': 'Atnaujinti',
  'action.close': 'Uždaryti',
  'action.back': 'Atgal',
  'action.next': 'Kitas',
  'action.previous': 'Ankstesnis',
  'action.viewAll': 'Peržiūrėti viską',
  'action.viewDetails': 'Peržiūrėti detales',
  'action.generate': 'Generuoti',
  'action.signOut': 'Atsijungti',
  'action.addNew': 'Pridėti naują',
  'action.invite': 'Pakviesti',

  // Statuses
  'status.draft': 'Juodraštis',
  'status.submitted': 'Pateikta',
  'status.validated': 'Patvirtinta',
  'status.approved': 'Patvirtinta',
  'status.rejected': 'Atmesta',
  'status.pending': 'Laukiama',
  'status.inProgress': 'Vykdoma',
  'status.overdue': 'Vėluojama',
  'status.compliant': 'Atitinkama',
  'status.filed': 'Pateikta',
  'status.active': 'Aktyvu',
  'status.inactive': 'Neaktyvu',
  'status.closed': 'Uždaryta',
  'status.fundraising': 'Lėšų rinkimas',
  'status.investing': 'Investavimas',
  'status.harvesting': 'Derliaus nuėmimas',
  'status.validating': 'Tikrinimas',
  'status.review': 'Peržiūroje',
  'status.monitoring': 'Stebėjimas',
  'status.exiting': 'Išėjimas',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Deramas patikrinimas',
  'status.disbursed': 'Išmokėta',
  'status.restructured': 'Restruktūrizuota',
  'status.matured': 'Suėjęs',
  'status.written_off': 'Nurašyta',
  'status.exited': 'Išėjęs',
  'status.incomplete': 'Nebaigta',
  'status.open': 'Atvira',
  'status.in_progress': 'Vykdoma',
  'status.completed': 'Baigta',
  'status.cancelled': 'Atšaukta',
  'status.accepted': 'Priimta',
  'status.failed': 'Nepavyko',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portfelio apžvalga',
  'dashboard.complianceStatus': 'Atitikties būsena',
  'dashboard.collectionProgress': 'Rinkimo progresas',
  'dashboard.activityFeed': 'Veikla',
  'dashboard.quickActions': 'Greiti veiksmai',
  'dashboard.investmentPipeline': 'Investicijų konvejeris',
  'dashboard.totalFunds': 'Iš viso fondų',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktyvios investicijos',
  'dashboard.totalInvestees': 'Investicijų gavėjai',
  'dashboard.recentActivity': 'Naujausia veikla',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Iš viso įsipareigota',
  'lp.capitalCalled': 'Pareikalauta kapitalo',
  'lp.distributed': 'Paskirstyta',
  'lp.netPosition': 'Grynoji pozicija',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fondų rezultatai',
  'lp.esgSummary': 'ESG Santrauka',
  'lp.reportLibrary': 'Ataskaitų biblioteka',
  'lp.taxonomyAligned': 'Suderinta su taksonomija',
  'lp.paiCoverage': 'PAI Aprėptis',
  'lp.portfolioPerformance': 'LP Portfelio rezultatai',

  // Fund Labels
  'fund.name': 'Fondo pavadinimas',
  'fund.code': 'Fondo kodas',
  'fund.vintage': 'Įsteigimo metai',
  'fund.size': 'Fondo dydis',
  'fund.targetSize': 'Tikslinis dydis',
  'fund.currency': 'Valiuta',
  'fund.strategy': 'Strategija',
  'fund.status': 'Būsena',
  'fund.sfdrCategory': 'SFDR Kategorija',
  'fund.geographyFocus': 'Geografinis fokusas',
  'fund.sectorFocus': 'Sektoriaus fokusas',

  // Form Labels
  'form.name': 'Pavadinimas',
  'form.email': 'El. paštas',
  'form.country': 'Šalis',
  'form.amount': 'Suma',
  'form.currency': 'Valiuta',
  'form.date': 'Data',
  'form.description': 'Aprašymas',
  'form.notes': 'Pastabos',
  'form.type': 'Tipas',
  'form.period': 'Laikotarpis',
  'form.year': 'Metai',
  'form.required': 'Privalomas',
  'form.optional': 'Neprivalomas',

  // Table Headers
  'table.fund': 'Fondas',
  'table.commitment': 'Įsipareigojimas',
  'table.called': 'Pareikalauta',
  'table.distributed': 'Paskirstyta',
  'table.tvpi': 'TVPI',
  'table.status': 'Būsena',
  'table.regulation': 'Reguliavimas',
  'table.filingType': 'Pateikimo tipas',
  'table.reportingPeriod': 'Laikotarpis',
  'table.createdAt': 'Sukurta',
  'table.actions': 'Veiksmai',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'ES Taksonomija',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Duomenų užbaigtumas',
  'compliance.nextDeadline': 'Kitas terminas',

  // Reports
  'report.quarterly': 'Ketvirtinė ataskaita',
  'report.annual': 'Metinė ataskaita',
  'report.paiStatement': 'PAI Pareiškimas',
  'report.taxonomyReport': 'Taksonomijos ataskaita',
  'report.generatedAt': 'Sugeneruota',
  'report.version': 'Versija',

  // General
  'general.loading': 'Kraunama...',
  'general.noData': 'Nėra prieinamų duomenų',
  'general.error': 'Įvyko klaida',
  'general.retry': 'Bandyti dar kartą',
  'general.total': 'Iš viso',
  'general.page': 'Puslapis',
  'general.of': 'iš',
  'general.showing': 'Rodoma',
  'general.results': 'rezultatai',
  'general.language': 'Kalba',
  'general.english': 'Anglų',
  'general.french': 'Prancūzų',

  // Tour
  'tour.takeTour': 'Pradeti apziura',
  'tour.skip': 'Praleisti',
  'tour.next': 'Kitas',
  'tour.prev': 'Ankstesnis',
  'tour.finish': 'Baigti',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigacija',
  'tour.sidebarContent': 'Naudokite šoninę juostą naviguoti tarp portfelio valdymo, ESG duomenų rinkimo, atitikties, ataskaitų ir analitikos įrankių.',
  'tour.kpisTitle': 'Portfelio apžvalga',
  'tour.kpisContent': 'Šios kortelės rodo jūsų portfelį iš pirmo žvilgsnio — iš viso fondų, AUM, aktyvios investicijos ir investuojamų subjektų skaičius.',
  'tour.actionsTitle': 'Greiti veiksmai',
  'tour.actionsContent': 'Kurkite fondus, pridėkite investicijas, pakvieskite subjektus ir generuokite ataskaitas vienu paspaudimu.',
  'tour.languageTitle': 'Kalba',
  'tour.languageContent': 'Perjunkite tarp 24 oficialių ES kalbų. Visa sąsaja išverčiama akimirksniu.',
  'tour.aiTitle': 'AI Asistentas',
  'tour.aiContent': 'Užduokite klausimus apie savo portfelį bet kuriuo metu. AI asistentas turi pilną kontekstą apie jūsų fondus ir investicijas.',
  'action.createFund': 'Sukurti fondą',
  'action.editFund': 'Redaguoti fondą',
  'action.addInvestment': 'Pridėti investiciją',
  'action.addInvestee': 'Pridėti subjektą',
  'action.editInvestee': 'Redaguoti subjektą',
  'action.createTemplate': 'Sukurti šabloną',
  'action.createSubmission': 'Sukurti pateikimą',
  'action.createFiling': 'Sukurti pateikimą',
  'action.generateReport': 'Generuoti ataskaitą',
  'action.inviteUser': 'Pakviesti naudotoją',
  'action.sendInvitation': 'Siųsti pakvietimą',
  'action.saveChanges': 'Išsaugoti pakeitimus',
  'form.fundName': 'Fondo pavadinimas',
  'form.fundCode': 'Fondo kodas',
  'form.sfdrCategory': 'SFDR Kategorija',
  'form.baseCurrency': 'Bazinė valiuta',
  'form.targetSize': 'Tikslinis dydis',
  'form.vintageYear': 'Įsteigimo metai',
  'form.geographyFocus': 'Geografinis fokusas',
  'form.sectorFocus': 'Sektoriaus fokusas',
  'form.investmentCode': 'Investicijos kodas',
  'form.instrumentType': 'Instrumento tipas',
  'form.maturityDate': 'Išpirkimo data',
  'form.sector': 'Sektorius',
  'form.countryCode': 'Šalies kodas',
  'form.entityType': 'Subjekto tipas',
  'form.region': 'Regionas',
  'form.staffCount': 'Darbuotojų skaičius',
  'form.borrowers': 'Skolininkai',
  'form.contactName': 'Kontaktinis vardas',
  'form.contactEmail': 'Kontaktinis el. paštas',
  'form.templateName': 'Šablono pavadinimas',
  'form.templateType': 'Šablono tipas',
  'form.indicators': 'Rodikliai',
  'form.reportingPeriod': 'Ataskaitinis laikotarpis',
  'form.reportingYear': 'Ataskaitiniai metai',
  'form.regulation': 'Reguliavimas',
  'form.filingType': 'Pateikimo tipas',
  'form.reportType': 'Ataskaitos tipas',
  'form.emailAddress': 'El. pašto adresas',
  'form.role': 'Vaidmuo',
  'form.selectFund': 'Pasirinkite fondą...',
  'form.selectInvestee': 'Pasirinkite subjektą...',
  'table.code': 'Kodas',
  'table.investee': 'Subjektas',
  'table.instrument': 'Instrumentas',
  'table.amount': 'Suma',
  'table.currency': 'Valiuta',
  'table.maturity': 'Išpirkimas',
  'table.name': 'Pavadinimas',
  'table.country': 'Šalis',
  'table.type': 'Tipas',
  'table.period': 'Laikotarpis',
  'table.completion': 'Užbaigimas',
  'table.submitted': 'Pateikta',
  'table.version': 'Versija',
  'table.indicators': 'Rodikliai',
  'table.dueDate': 'Terminas',
  'table.generated': 'Sugeneruota',
  'table.email': 'El. paštas',
  'table.role': 'Vaidmuo',
  'error.createFailed': 'Nepavyko sukurti. Bandykite dar kartą.',
  'error.updateFailed': 'Nepavyko atnaujinti. Bandykite dar kartą.',
  'error.loadFailed': 'Nepavyko įkelti duomenų.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFELIS',
  'sections.dataCollection': 'DUOMENŲ RINKIMAS',
  'sections.intelligence': 'ŽVALGYBA',
  'sections.ecosystem': 'EKOSISTEMA',
  'sections.admin': 'ADMINISTRAVIMAS',
  'sections.other': 'KITA',

  // Page Titles
  'pages.messagesTitle': 'Žinutės',
  'pages.taxonomyTitle': 'ES Taksonomija',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Žinių grafas',
  'pages.benchmarksTitle': 'Lyginamieji rodikliai',
  'pages.mcpTitle': 'MCP integracija',
  'pages.tasksTitle': 'Užduotys',
  'pages.submitDataTitle': 'Pateikti ESG duomenis',

  // Empty States
  'empty.benchmarksComingSoon': 'Lyginamieji rodikliai netrukus',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'ES Taksonomija',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Kita',
  'labels.critical': 'Kritinis',
  'labels.high': 'Aukštas',
  'labels.medium': 'Vidutinis',
  'labels.low': 'Žemas',

  // Count / summary strings
  'count.fundsTotal': '{count} fund(s) total',
  'count.investmentsTotal': '{count} investment(s) total',
  'count.investeesTotal': '{count} investee(s) total',

  // Filter options
  'filter.allStatus': 'All Status',
  'filter.allSfdr': 'All SFDR',

  // Empty states
  'empty.noFundsYet': 'No funds yet. Create your first fund.',
  'empty.noFundsMatch': 'No funds match your filters.',
  'empty.noInvestmentsYet': 'No investments yet.',
  'empty.noInvestmentsMatch': 'No investments match your search.',
  'empty.noInvesteesYet': 'No investees yet.',
  'empty.noInvesteesMatch': 'No investees match your search.',
  'empty.noTemplatesYet': 'No ESG templates configured yet.',
  'empty.noSubmissionsYet': 'No ESG submissions yet.',
  'empty.noComplianceData': 'No SFDR compliance data available.',
  'empty.noFilingsYet': 'No compliance filings yet.',
  'empty.noReportsYet': 'No reports generated yet.',
  'empty.selectFundPai': 'Select a fund to view PAI indicators.',
  'empty.noPaiIndicators': 'No PAI indicators found for this fund.',
  'empty.selectFundCalendar': 'Select a fund to view compliance calendar.',
  'empty.noCalendarEvents': 'No upcoming events for this fund.',

  // Page subtitles
  'subtitle.esg': 'Templates and data submissions',
  'subtitle.compliance': 'SFDR, PAI indicators, and regulatory filings',
  'subtitle.reports': 'Generate and manage compliance reports',
  'subtitle.settings': 'Manage your account and preferences',

  // Tab labels
  'tab.templates': 'Templates',
  'tab.submissions': 'Submissions',
  'tab.sfdr': 'SFDR',
  'tab.paiIndicators': 'PAI Indicators',
  'tab.filings': 'Filings',
  'tab.reports': 'Reports',
  'tab.calendar': 'Calendar',

  // Compliance table headers
  'table.indicator': 'Indicator',
  'table.category': 'Category',
  'table.metric': 'Metric',
  'table.value': 'Value',
  'table.mandatory': 'Mandatory',
  'table.data': 'Data',
  'table.deadline': 'Deadline',
  'table.taxonomy': 'Taxonomy',

  // Yes/No
  'general.yes': 'Yes',
  'general.no': 'No',

  // Error messages
  'error.loadTemplates': 'Failed to load templates.',
  'error.loadSubmissions': 'Failed to load submissions.',
  'error.loadCompliance': 'Failed to load compliance data.',
  'error.loadPai': 'Failed to load PAI indicators.',
  'error.loadFilings': 'Failed to load filings.',
  'error.loadCalendar': 'Failed to load calendar events.',
  'error.createTemplate': 'Failed to create template. Please try again.',
  'error.createSubmission': 'Failed to create submission. Please try again.',
  'error.createFiling': 'Failed to create filing. Please try again.',
  'error.generateReport': 'Failed to generate report.',
  'error.templateNameRequired': 'Template name is required',
  'error.selectFund': 'Select a fund',
  'error.selectInvestee': 'Select an investee',
  'error.validAmount': 'Enter a valid amount',
  'error.reportingPeriodRequired': 'Reporting period is required',
  'error.validYearRequired': 'Valid year is required',
  'error.enterPeriod': 'Enter a period',
  'error.nameRequired': 'Name is required',
  'error.countryCodeRequired': 'Country code is required',
  'error.operationFailed': 'Operation failed.',
  'error.pleaseselectFund': 'Please select a fund',

  // Success messages
  'success.submissionCreated': 'Submission created successfully.',
  'success.profileSaved': 'Profile saved successfully.',

  // Relative time
  'time.justNow': 'Just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',

  // Settings
  'settings.profile': 'Profile',
  'settings.organization': 'Organization',
  'settings.apiKeys': 'API Keys',
  'settings.notifications': 'Notifications',
  'settings.orgId': 'Org ID',
  'settings.tier': 'Tier',
  'settings.apiKeysDescription': 'Contact your organization administrator to manage API keys and integration credentials.',
  'settings.apiKeysNote': 'API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.',
  'settings.notifReportAlerts': 'Report generation alerts',
  'settings.notifReportAlertsDesc': 'Email when a report is generated or fails',
  'settings.notifComplianceReminders': 'Compliance deadline reminders',
  'settings.notifComplianceRemindersDesc': 'Email reminders for upcoming filing deadlines',
  'settings.notifEsgUpdates': 'ESG submission updates',
  'settings.notifEsgUpdatesDesc': 'Email when investee submissions change status',
  'settings.notifInAppActivity': 'In-app activity feed',
  'settings.notifInAppActivityDesc': 'Show real-time activity notifications in header',

  // Data Completeness label
  'compliance.dataCompletenessLabel': 'Data Completeness',
};
